import { queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  humanizeStatus,
  TASK_STATUS_TONE,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import type { CustomerLite } from './customer-picker.js'
import { LinkifiedText } from './linkified-text.js'
import {
  CompleteTaskDialog,
  DueDate,
  EditTaskDialog,
  groupTaskThreads,
  isScheduled,
  NewTaskDialog,
  ReviewActions,
  StartDate,
  type TaskRow,
  useTaskToggle,
} from './task-shared.js'

/**
 * Follow-ups on one support ticket: tasks created from here (or linked to it)
 * that someone still has to do. Lives on the ticket so the next person to open
 * it sees what is owed, and also appears in the main Tasks list. A follow-up
 * on a follow-up is shown indented under the one it follows up on.
 */
export function TicketFollowUps({
  ticketId,
  customer,
}: {
  ticketId: string
  /** The ticket's customer, pre-filled on new follow-ups (t1369). */
  customer?: CustomerLite
}) {
  const { can } = useAuth()
  const canRead = can('tasks:read')
  const canWrite = can('tasks:write')
  const canReview = can('tasks:review')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<TaskRow | null>(null)
  const [completing, setCompleting] = useState<TaskRow | null>(null)
  const [parentFor, setParentFor] = useState<TaskRow | null>(null)
  const toggle = useTaskToggle()

  const filters = { supportTicketId: ticketId, status: 'all', scheduled: 'include', pageSize: 50 }
  const { data } = useQuery({
    queryKey: queryKeys.tasks.list(filters),
    queryFn: () => api.get<{ items: TaskRow[] }>('/tasks', { query: filters }),
    enabled: canRead,
    refetchInterval: 15_000,
  })

  const [showScheduled, setShowScheduled] = useState(false)
  // Tasks whose start date is still ahead stay out of sight until they start (t1348).
  const scheduledCount = (data?.items ?? []).filter(isScheduled).length
  const items = useMemo(() => {
    const all = data?.items ?? []
    return groupTaskThreads(showScheduled ? all : all.filter((t) => !isScheduled(t)))
  }, [data, showScheduled])
  if (!canRead) return null

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Follow-ups</CardTitle>
        {canWrite ? (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus /> Add
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {items.length === 0 && scheduledCount === 0 ? (
          <p className="text-sm text-foreground-muted">
            Nothing to follow up on. Add a reminder to call back or check in.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((task) => (
              <li
                key={task.id}
                className={`text-sm ${task.depth > 0 ? 'border-s-2 border-border ps-3' : ''}`}
                style={task.depth > 1 ? { marginInlineStart: (task.depth - 1) * 12 } : undefined}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={task.status === 'done' ? 'text-foreground-muted line-through' : ''}>
                    {task.title}
                  </p>
                  <Badge tone={TASK_STATUS_TONE[task.status] ?? 'neutral'}>
                    {humanizeStatus(task.status)}
                  </Badge>
                </div>
                {task.orphan && task.parentTaskNumber ? (
                  <p className="text-xs text-foreground-muted">
                    ↳ follow-up of <span className="font-identifier">{task.parentTaskNumber}</span>
                  </p>
                ) : null}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-foreground-muted">
                  <span>{task.assignedStaffId ? task.assignedStaffName : 'Everyone'}</span>
                  <StartDate task={task} />
                  <DueDate task={task} />
                  {canWrite && task.status === 'pending_review' && canReview ? (
                    <ReviewActions task={task} variant="link" />
                  ) : null}
                  {canWrite && !(task.status === 'pending_review' && canReview) ? (
                    <button
                      type="button"
                      className="text-primary hover:underline disabled:opacity-50"
                      disabled={toggle.isPending}
                      onClick={() =>
                        task.status === 'open'
                          ? setCompleting(task)
                          : toggle.mutate({ id: task.id, action: 'reopen' })
                      }
                    >
                      {task.status === 'open' ? 'Mark done' : 'Reopen'}
                    </button>
                  ) : null}
                  {canWrite ? (
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setParentFor(task)}
                    >
                      Add follow-up
                    </button>
                  ) : null}
                  {canWrite ? (
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setEditing(task)}
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
                {task.status !== 'open' && task.completionNote ? (
                  <p className="mt-1 whitespace-pre-wrap text-xs text-foreground-muted">
                    “<LinkifiedText text={task.completionNote} />”
                    {task.completedByName ? ` — ${task.completedByName}` : ''}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {scheduledCount > 0 ? (
          <button
            type="button"
            className="mt-3 text-xs text-primary hover:underline"
            onClick={() => setShowScheduled((v) => !v)}
          >
            {showScheduled ? 'Hide scheduled tasks' : `${scheduledCount} scheduled for later`}
          </button>
        ) : null}
      </CardContent>
      <NewTaskDialog
        open={adding}
        onOpenChange={setAdding}
        supportTicketId={ticketId}
        customer={customer}
      />
      <NewTaskDialog
        open={parentFor !== null}
        onOpenChange={(open) => !open && setParentFor(null)}
        parentTask={parentFor}
      />
      <CompleteTaskDialog task={completing} onClose={() => setCompleting(null)} />
      <EditTaskDialog task={editing} onOpenChange={(open) => !open && setEditing(null)} />
    </Card>
  )
}
